import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CodeBracketIcon, CursorArrowRaysIcon } from '@heroicons/react/24/outline';
import { apiHelpers } from '../../services/api';
import { useDashboardStore } from '../../store/dashboardStore';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import VisualChartBuilder from '../charts/VisualChartBuilder';

const chartTypes = [
  { id: 'bar', name: 'Bar Chart', icon: '📊' },
  { id: 'line', name: 'Line Chart', icon: '📈' },
  { id: 'pie', name: 'Pie Chart', icon: '🥧' },
  { id: 'donut', name: 'Donut Chart', icon: '🍩' },
  { id: 'area', name: 'Area Chart', icon: '📉' },
  { id: 'scatter', name: 'Scatter Plot', icon: '🔵' },
  { id: 'heatmap', name: 'Heatmap', icon: '🔥' },
  { id: 'geo', name: 'Geo Map', icon: '🌍' },
  { id: 'table', name: 'Table', icon: '📋' },
  { id: 'kpi', name: 'KPI Card', icon: '🎯' }
];

export default function AddChartModal({ isOpen, onClose, dashboardId }) {
  const [step, setStep] = useState(1);
  const [chartType, setChartType] = useState('');
  const [dataSources, setDataSources] = useState([]);
  const [wideViews, setWideViews] = useState([]);
  const [marts, setMarts] = useState([]);
  const [selectedDataSource, setSelectedDataSource] = useState('');
  const [selectedSourceType, setSelectedSourceType] = useState('datasource'); // 'datasource', 'wideview', or 'mart'
  const [schema, setSchema] = useState(null);
  const [chartName, setChartName] = useState('');
  const [query, setQuery] = useState('');
  const [xAxisLabel, setXAxisLabel] = useState('');
  const [yAxisLabel, setYAxisLabel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tableName, setTableName] = useState('');
  
  // Builder mode: 'visual' or 'sql'
  const [builderMode, setBuilderMode] = useState('visual');
  const [visualConfig, setVisualConfig] = useState(null);
  
  const { addChart } = useDashboardStore();
  const { getCurrentCompanyId, isSuperUser } = useAuthStore();
  const [noCompanySelected, setNoCompanySelected] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      fetchChartSources();
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (selectedDataSource) {
      fetchSchema();
    }
  }, [selectedDataSource, selectedSourceType]);
  
  const fetchChartSources = async () => {
    try {
      const companyId = getCurrentCompanyId();
      if (!companyId && isSuperUser()) {
        setNoCompanySelected(true);
        setDataSources([]);
        setWideViews([]);
        setMarts([]);
        return;
      }
      setNoCompanySelected(false);
      const response = await apiHelpers.getChartSources({ companyId });
      setDataSources(response.data.dataSources || []);
      setWideViews(response.data.wideViews || []);
      setMarts(response.data.marts || []);
    } catch (error) {
      console.error('Failed to fetch chart sources:', error);
    }
  };
  
  const fetchSchema = async () => {
    try {
      if (selectedSourceType === 'mart') {
        // For marts, use the schema from the marts state
        const m = marts.find(mart => mart._id === selectedDataSource);
        if (m) {
          setSchema(m.schemaInfo);
          // Generate query using dbt model name (marts are in _marts dataset)
          const modelName = m.dbtModelName || `mart_${m._id.replace('mart_', '')}`;
          setTableName(modelName);
          setQuery(`SELECT * FROM \${dataset}_marts.${modelName} LIMIT 100`);
        }
      } else if (selectedSourceType === 'wideview') {
        // For wide views, use the schema from the wideViews state
        const wv = wideViews.find(w => w._id === selectedDataSource);
        if (wv) {
          setSchema(wv.schemaInfo);
          // Generate query using dbt model name
          const modelName = wv.dbtModelName || `wide_${wv._id.replace('wide_', '')}`;
          setTableName(modelName);
          setQuery(`SELECT * FROM \${dataset}_wide_views.${modelName} LIMIT 100`);
        }
      } else {
        // For regular data sources, fetch schema from API
        const response = await apiHelpers.getDataSourceSchema(selectedDataSource);
        setSchema(response.data.schema);
        
        // Generate default query
        const table = response.data.bigQueryTable;
        if (table) {
          setTableName(table);
          setQuery(`SELECT * FROM \${dataset}.${table} LIMIT 100`);
        }
      }
    } catch (error) {
      console.error('Failed to fetch schema:', error);
    }
  };
  
  const handleSourceSelect = (sourceId) => {
    const isMart = sourceId.startsWith('mart_');
    const isWideView = sourceId.startsWith('wide_');
    setSelectedDataSource(sourceId);
    setSelectedSourceType(isMart ? 'mart' : (isWideView ? 'wideview' : 'datasource'));
    setSchema(null);
    setQuery('');
    setVisualConfig(null);
  };
  
  // Handle query generation from visual builder
  const handleQueryGenerated = (generatedQuery, config) => {
    setQuery(generatedQuery);
    setVisualConfig(config);
  };
  
  const handleCreate = async () => {
    if (!chartName || !chartType || !selectedDataSource || !query) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const chartData = {
        name: chartName,
        type: chartType,
        dashboardId,
        query,
        // Include chart config with axis labels
        chartConfig: {
          xAxis: {
            label: xAxisLabel || undefined
          },
          yAxis: {
            label: yAxisLabel || undefined
          },
          // Store visual config for future editing
          visualConfig: builderMode === 'visual' ? visualConfig : undefined
        }
      };
      
      // Handle different source types
      if (selectedSourceType === 'mart') {
        const m = marts.find(mart => mart._id === selectedDataSource);
        chartData.martId = selectedDataSource.replace('mart_', '');
        chartData.dbtModelName = m?.dbtModelName;
        chartData.isMart = true;
      } else if (selectedSourceType === 'wideview') {
        const wv = wideViews.find(w => w._id === selectedDataSource);
        chartData.wideViewName = selectedDataSource.replace('wide_', '');
        chartData.dbtModelName = wv?.dbtModelName;
        // For wide views, we may not have a dataSourceId, use the base table's data source if available
        chartData.isWideView = true;
      } else {
        chartData.dataSourceId = selectedDataSource;
      }
      
      await addChart(chartData);
      
      toast.success('Chart created!');
      handleClose();
    } catch (error) {
      toast.error('Failed to create chart');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClose = () => {
    setStep(1);
    setChartType('');
    setSelectedDataSource('');
    setSelectedSourceType('datasource');
    setSchema(null);
    setChartName('');
    setQuery('');
    setXAxisLabel('');
    setYAxisLabel('');
    setNoCompanySelected(false);
    setWideViews([]);
    setMarts([]);
    setBuilderMode('visual');
    setVisualConfig(null);
    setTableName('');
    onClose();
  };
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full transform overflow-hidden rounded-2xl bg-white dark:bg-surface-900 shadow-2xl transition-all ${step === 2 ? 'max-w-4xl' : 'max-w-2xl'}`}>
                <div className="flex items-center justify-between p-6 border-b border-surface-200 dark:border-surface-800">
                  <Dialog.Title className="text-lg font-display font-semibold text-surface-900 dark:text-white">
                    Add Chart
                  </Dialog.Title>
                  <button onClick={handleClose} className="p-1 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800">
                    <XMarkIcon className="w-5 h-5 text-surface-500" />
                  </button>
                </div>
                
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  {/* Step 1: Choose chart type */}
                  {step === 1 && (
                    <div className="space-y-4">
                      <p className="text-surface-600 dark:text-surface-400">
                        Choose a chart type
                      </p>
                      <div className="grid grid-cols-5 gap-3">
                        {chartTypes.map(type => (
                          <button
                            key={type.id}
                            onClick={() => {
                              setChartType(type.id);
                              setStep(2);
                            }}
                            className={`p-4 rounded-xl border-2 text-center transition-all hover:border-primary-500 hover:scale-105 ${
                              chartType === type.id 
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' 
                                : 'border-surface-200 dark:border-surface-700'
                            }`}
                          >
                            <span className="text-2xl mb-2 block">{type.icon}</span>
                            <span className="text-xs font-medium text-surface-700 dark:text-surface-300">
                              {type.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Step 2: Configure chart */}
                  {step === 2 && (
                    <div className="space-y-4">
                      {/* Chart Name & Data Source Row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="label">Chart Name</label>
                          <input
                            type="text"
                            value={chartName}
                            onChange={(e) => setChartName(e.target.value)}
                            placeholder="Enter chart name"
                            className="input"
                          />
                        </div>
                        
                        <div>
                          <label className="label">Data Source</label>
                          {noCompanySelected ? (
                            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                              <p className="text-sm text-amber-800 dark:text-amber-200">
                                Please select a company first.
                              </p>
                            </div>
                          ) : (
                          <select
                            value={selectedDataSource}
                            onChange={(e) => handleSourceSelect(e.target.value)}
                            className="input"
                          >
                            <option value="">Select a data source</option>
                            {marts.length > 0 && (
                              <optgroup label="⭐ Marts (Analytics-Ready)">
                                {marts.map(m => (
                                  <option key={m._id} value={m._id}>
                                    {m.name}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {dataSources.length > 0 && (
                              <optgroup label="📊 Data Sources">
                            {dataSources.map(ds => (
                              <option key={ds._id} value={ds._id}>
                                {ds.name}
                              </option>
                            ))}
                              </optgroup>
                            )}
                            {wideViews.length > 0 && (
                              <optgroup label="🔗 Wide Views (Joined Tables)">
                                {wideViews.map(wv => (
                                  <option key={wv._id} value={wv._id}>
                                    {wv.name}
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                          )}
                        </div>
                      </div>
                      
                      {selectedDataSource && schema && (
                        <>
                          {/* Builder Mode Toggle */}
                          <div className="flex items-center justify-between bg-surface-100 dark:bg-surface-800 rounded-xl p-2">
                            <div className="flex gap-2">
                              <button
                                onClick={() => setBuilderMode('visual')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                  builderMode === 'visual' 
                                    ? 'bg-white dark:bg-surface-700 text-primary-600 shadow-sm' 
                                    : 'text-surface-600 dark:text-surface-400 hover:text-surface-900'
                                }`}
                              >
                                <CursorArrowRaysIcon className="w-4 h-4" />
                                Visual Builder
                              </button>
                              <button
                                onClick={() => setBuilderMode('sql')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                  builderMode === 'sql' 
                                    ? 'bg-white dark:bg-surface-700 text-primary-600 shadow-sm' 
                                    : 'text-surface-600 dark:text-surface-400 hover:text-surface-900'
                                }`}
                              >
                                <CodeBracketIcon className="w-4 h-4" />
                                SQL Editor
                              </button>
                            </div>
                            {selectedSourceType === 'mart' && (
                              <span className="text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-2 py-1 rounded">
                                ⭐ Using Mart
                              </span>
                            )}
                          </div>
                          
                          {/* Visual Builder Mode */}
                          {builderMode === 'visual' && (
                            <VisualChartBuilder
                              schema={schema}
                              chartType={chartType}
                              sourceType={selectedSourceType}
                              tableName={tableName}
                              onQueryGenerated={handleQueryGenerated}
                              initialConfig={visualConfig}
                            />
                          )}
                          
                          {/* SQL Editor Mode */}
                          {builderMode === 'sql' && (
                            <div className="space-y-4">
                              {/* Available Columns Reference */}
                              <div>
                                <label className="label">Available Columns</label>
                                <div className="flex flex-wrap gap-2 p-3 bg-surface-50 dark:bg-surface-800 rounded-lg max-h-24 overflow-y-auto">
                                  {schema.tables?.[0]?.columns?.map(col => (
                                    <button
                                      key={col.name}
                                      onClick={() => {
                                        // Insert column name at cursor position or append
                                        setQuery(prev => prev + col.name);
                                      }}
                                      className="badge-primary cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-800"
                                      title="Click to insert"
                                    >
                                      {col.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              
                              <div>
                                <label className="label">SQL Query</label>
                                <textarea
                                  value={query}
                                  onChange={(e) => setQuery(e.target.value)}
                                  placeholder="SELECT * FROM ${dataset}.table_name"
                                  rows={6}
                                  className="input font-mono text-sm"
                                />
                                <p className="text-xs text-surface-500 mt-1">
                                  Use ${'{dataset}'} as the dataset placeholder. 
                                  For marts use ${'{dataset}_marts'}, for wide views use ${'{dataset}_wide_views'}.
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Generated SQL Preview (in visual mode) */}
                          {builderMode === 'visual' && query && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="label mb-0">Generated SQL</label>
                                <button
                                  onClick={() => setBuilderMode('sql')}
                                  className="text-xs text-primary-600 hover:underline"
                                >
                                  Edit SQL directly →
                                </button>
                              </div>
                              <pre className="p-3 bg-surface-900 text-surface-100 rounded-lg text-xs font-mono overflow-x-auto max-h-32">
                                {query}
                              </pre>
                            </div>
                          )}
                          
                          {/* Axis Labels - only show for chart types with axes */}
                          {!['pie', 'donut', 'table', 'kpi'].includes(chartType) && (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="label">X-Axis Label (optional)</label>
                                <input
                                  type="text"
                                  value={xAxisLabel}
                                  onChange={(e) => setXAxisLabel(e.target.value)}
                                  placeholder="Auto-generated if empty"
                                  className="input"
                                />
                              </div>
                              <div>
                                <label className="label">Y-Axis Label (optional)</label>
                                <input
                                  type="text"
                                  value={yAxisLabel}
                                  onChange={(e) => setYAxisLabel(e.target.value)}
                                  placeholder="Auto-generated if empty"
                                  className="input"
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {!selectedDataSource && (
                        <div className="text-center py-8 text-surface-500">
                          <p>Select a data source to configure your chart</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between p-6 border-t border-surface-200 dark:border-surface-800">
                  {step === 2 && (
                    <button
                      onClick={() => setStep(1)}
                      className="btn-secondary"
                    >
                      Back
                    </button>
                  )}
                  {step === 1 && <div />}
                  
                  {step === 2 && (
                    <button
                      onClick={handleCreate}
                      disabled={isLoading || !chartName || !selectedDataSource || !query}
                      className="btn-primary"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      ) : null}
                      Create Chart
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
